import {
  defaultStoreHours,
  GROCERY_WEEK_DAYS,
  type StoreHourRow,
} from '../../utils/groceryMasterImages';

export type GroceryVerificationStatus = 'pending' | 'approved' | 'rejected' | 'blocked';
export type GroceryRevenueModel = 'commission' | 'subscription';

export interface GroceryStoreFormState {
  name: string;
  slug: string;
  description: string;
  ownerName: string;
  phone: string;
  email: string;
  supportNumber: string;
  merchantId: string;
  logo: string;
  coverImage: string;
  addressLine: string;
  city: string;
  state: string;
  pincode: string;
  latitude: string;
  longitude: string;
  zoneCode: string;
  storeTypeCode: string;
  warehouseCode: string;
  gstNumber: string;
  fssaiNumber: string;
  tradeLicenseNumber: string;
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  isDarkStore: boolean;
  minOrderAmount: number;
  deliveryRadius: number;
  avgPickTimeMinutes: number;
  openingHours: StoreHourRow[];
  weeklyHoliday: string;
  verificationStatus: GroceryVerificationStatus;
  revenueModel: GroceryRevenueModel;
  vendorCommissionType: 'percentage' | 'fixed';
  vendorCommissionValue: number;
  partnerSubscriptionPlanId: string;
  isOpen: boolean;
  isActive: boolean;
}

export const GROCERY_STORE_TABS = [
  { id: 'basic', label: 'Basic Details' },
  { id: 'business', label: 'Business' },
  { id: 'settings', label: 'Settings' },
  { id: 'timing', label: 'Timing' },
  { id: 'approval', label: 'Approval & Revenue' },
  { id: 'documents', label: 'Documents' },
] as const;

export const VERIFICATION_STATUS_OPTIONS: {
  value: GroceryVerificationStatus;
  label: string;
  color: 'warning' | 'success' | 'error' | 'default';
}[] = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'approved', label: 'Approved', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
  { value: 'blocked', label: 'Blocked', color: 'default' },
];

export { GROCERY_WEEK_DAYS };

export const emptyGroceryStoreForm = (): GroceryStoreFormState => ({
  name: '',
  slug: '',
  description: '',
  ownerName: '',
  phone: '',
  email: '',
  supportNumber: '',
  merchantId: '',
  logo: '',
  coverImage: '',
  addressLine: '',
  city: 'Tiruppur',
  state: 'Tamil Nadu',
  pincode: '641601',
  latitude: '11.1085',
  longitude: '77.3411',
  zoneCode: '',
  storeTypeCode: 'hypermarket',
  warehouseCode: '',
  gstNumber: '',
  fssaiNumber: '',
  tradeLicenseNumber: '',
  deliveryEnabled: true,
  pickupEnabled: false,
  isDarkStore: false,
  minOrderAmount: 99,
  deliveryRadius: 8,
  avgPickTimeMinutes: 20,
  openingHours: defaultStoreHours(),
  weeklyHoliday: '',
  verificationStatus: 'pending',
  revenueModel: 'commission',
  vendorCommissionType: 'percentage',
  vendorCommissionValue: 15,
  partnerSubscriptionPlanId: '',
  isOpen: true,
  isActive: true,
});

export function storeRowToForm(row: Record<string, unknown>): GroceryStoreFormState {
  const meta = (row.metadata || {}) as Record<string, unknown>;
  const addr = (row.address || {}) as Record<string, unknown>;
  const coords = (row.location as { coordinates?: number[] })?.coordinates || [];
  const modes = (row.serviceModes || {}) as Record<string, boolean>;
  const hours = (row.openingHours as StoreHourRow[]) || (meta.openingHours as StoreHourRow[]) || [];
  const zone = row.zoneId as { code?: string } | string | undefined;
  const zoneCode = typeof zone === 'object' && zone?.code
    ? zone.code
    : String(row.zoneCode || meta.zoneCode || '');

  return {
    name: String(row.name || ''),
    slug: String(row.slug || ''),
    description: String(row.description || ''),
    ownerName: String(row.ownerName || meta.ownerName || ''),
    phone: String(row.phone || ''),
    email: String(row.email || ''),
    supportNumber: String(row.supportNumber || ''),
    merchantId: String((row.merchantId as { _id?: string })?._id || row.merchantId || ''),
    logo: String(row.logo || ''),
    coverImage: String(row.coverImage || ''),
    addressLine: String(addr.line1 || addr.street || addr.address || addr.area || ''),
    city: String(addr.city || ''),
    state: String(addr.state || ''),
    pincode: String(addr.pincode || ''),
    latitude: coords[1] != null ? String(coords[1]) : '',
    longitude: coords[0] != null ? String(coords[0]) : '',
    zoneCode,
    storeTypeCode: String(row.storeType || meta.storeTypeCode || 'hypermarket'),
    warehouseCode: String(meta.warehouseCode || ''),
    gstNumber: String(row.gstNumber || meta.gstNumber || ''),
    fssaiNumber: String(row.fssaiNumber || meta.fssaiNumber || ''),
    tradeLicenseNumber: String(row.tradeLicenseNumber || meta.tradeLicenseNumber || ''),
    deliveryEnabled: modes.delivery !== false,
    pickupEnabled: modes.pickup === true,
    isDarkStore: row.isDarkStore === true || row.storeType === 'dark_store',
    minOrderAmount: Number(row.minOrderAmount || 99),
    deliveryRadius: Number(row.deliveryRadius || 8),
    avgPickTimeMinutes: Number(row.avgPickTimeMinutes || 20),
    openingHours: hours.length ? hours : defaultStoreHours(),
    weeklyHoliday: String(row.weeklyHoliday || ''),
    verificationStatus: (() => {
      const raw = String(row.verificationStatus || 'pending');
      return (raw === 'verified' ? 'approved' : raw) as GroceryVerificationStatus;
    })(),
    revenueModel: (String(row.revenueModel || 'commission') as GroceryRevenueModel),
    vendorCommissionType: (row.vendorCommissionType as 'percentage' | 'fixed') || 'percentage',
    vendorCommissionValue: Number(row.vendorCommissionValue ?? 15),
    partnerSubscriptionPlanId: String(
      (row.partnerSubscriptionPlanId as { _id?: string })?._id || row.partnerSubscriptionPlanId || '',
    ),
    isOpen: row.isOpen !== false,
    isActive: row.isActive !== false,
  };
}

export function storeFormToPayload(form: GroceryStoreFormState) {
  return {
    name: form.name,
    slug: form.slug || form.name.toLowerCase().replace(/\s+/g, '-'),
    description: form.description || undefined,
    ownerName: form.ownerName || undefined,
    storeType: form.storeTypeCode,
    storeTypeCode: form.storeTypeCode,
    phone: form.phone || undefined,
    email: form.email || undefined,
    supportNumber: form.supportNumber || undefined,
    merchantId: form.merchantId || undefined,
    logo: form.logo || undefined,
    coverImage: form.coverImage || undefined,
    openingHours: form.openingHours,
    weeklyHoliday: form.weeklyHoliday || undefined,
    isOpen: form.isOpen,
    isActive: form.isActive,
    isDarkStore: form.isDarkStore || form.storeTypeCode === 'dark_store',
    deliveryEnabled: form.deliveryEnabled,
    pickupEnabled: form.pickupEnabled,
    minOrderAmount: Number(form.minOrderAmount),
    deliveryRadius: Number(form.deliveryRadius),
    avgPickTimeMinutes: Number(form.avgPickTimeMinutes),
    gstNumber: form.gstNumber || undefined,
    fssaiNumber: form.fssaiNumber || undefined,
    tradeLicenseNumber: form.tradeLicenseNumber || undefined,
    verificationStatus: form.verificationStatus === 'verified' ? 'approved' : form.verificationStatus,
    revenueModel: form.revenueModel,
    vendorCommissionType: form.vendorCommissionType,
    vendorCommissionValue: Number(form.vendorCommissionValue),
    partnerSubscriptionPlanId: form.partnerSubscriptionPlanId || undefined,
    zoneCode: form.zoneCode || undefined,
    addressLine: form.addressLine || undefined,
    city: form.city || undefined,
    state: form.state || undefined,
    pincode: form.pincode || undefined,
    latitude: form.latitude ? Number(form.latitude) : undefined,
    longitude: form.longitude ? Number(form.longitude) : undefined,
    address: {
      line1: form.addressLine || undefined,
      street: form.addressLine || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      pincode: form.pincode || undefined,
      country: 'IN',
    },
    metadata: {
      storeTypeCode: form.storeTypeCode,
      warehouseCode: form.warehouseCode || undefined,
      openingHours: form.openingHours,
      zoneCode: form.zoneCode || undefined,
    },
  };
}
